/**
 * GENERATED from the calculation registry — do not edit.
 *
 * Source of truth: appretio/v2/repositories/cube/engine/expressions.py, via
 * calc-registry/derived-metrics.json. Regenerate with
 *   REGEN_CALC_REGISTRY=1 uv run pytest tests/parity/test_calc_registry.py
 * (from packages/api). A freshness test fails CI when this file is stale.
 *
 * Display semantics: null operands and non-finite results yield null
 * (rendered blank), matching the legacy math.js value getter. Entries are
 * topologically sorted — dependencies always precede dependents, so a
 * single pass in insertion order resolves chained metrics.
 */

export interface GeneratedDerivedExpression {
  /** Operands: each is a metric_name whose row value to look up. */
  variables: string[];
  /** Only meaningful at weekly granularity (option-count style metrics). */
  weeklyOnly?: boolean;
  /** Evaluate from resolved variable values (same order as `variables`). */
  evaluate: (values: (number | null)[]) => number | null;
}

function div(a: number | null, b: number | null): number | null {
  if (a == null || b == null || b === 0) return null;
  return a / b;
}

function add(a: number | null, b: number | null): number | null {
  if (a == null || b == null) return null;
  return a + b;
}

function sub(a: number | null, b: number | null): number | null {
  if (a == null || b == null) return null;
  return a - b;
}

export const DERIVED_KPI_EXPRESSIONS: Record<
  string,
  GeneratedDerivedExpression
> = {
  OPTION_DEPTH: {
    variables: [
      "END_OF_PERIOD_UNITS",
      "OPTION_WIDTH"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_DENSITY: {
    variables: [
      "OPTION_WIDTH",
      "STORE_SQM"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_WIDTH_INTAKE_TOTAL: {
    variables: [
      "OPTION_WIDTH_INTAKE_MAIN",
      "OPTION_WIDTH_INTAKE_REPLENISHMENT",
      "OPTION_WIDTH_INTAKE_INSEASON",
      "OPTION_WIDTH_INTAKE_CLEARANCE"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1, a2, a3]) => sub(add(add(a0, a1), a2), a3)
  },
  OPTION_DEPTH_INTAKE_TOTAL: {
    variables: [
      "INTAKE_UNITS_TOTAL",
      "OPTION_WIDTH_INTAKE_TOTAL"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_DEPTH_INTAKE_MAIN: {
    variables: [
      "INTAKE_UNITS_MAIN",
      "OPTION_WIDTH_INTAKE_MAIN"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_DEPTH_INTAKE_REPLENISHMENT: {
    variables: [
      "INTAKE_UNITS_REPLENISHMENT",
      "OPTION_WIDTH_INTAKE_REPLENISHMENT"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_DEPTH_INTAKE_INSEASON: {
    variables: [
      "INTAKE_UNITS_INSEASON",
      "OPTION_WIDTH_INTAKE_INSEASON"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_DEPTH_INTAKE_CLEARANCE: {
    variables: [
      "INTAKE_UNITS_CLEARANCE",
      "OPTION_WIDTH_INTAKE_CLEARANCE"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_DENSITY_INTAKE_TOTAL: {
    variables: [
      "OPTION_WIDTH_INTAKE_TOTAL",
      "STORE_SQM"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_DENSITY_INTAKE_MAIN: {
    variables: [
      "OPTION_WIDTH_INTAKE_MAIN",
      "STORE_SQM"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_DENSITY_INTAKE_REPLENISHMENT: {
    variables: [
      "OPTION_WIDTH_INTAKE_REPLENISHMENT",
      "STORE_SQM"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_DENSITY_INTAKE_INSEASON: {
    variables: [
      "OPTION_WIDTH_INTAKE_INSEASON",
      "STORE_SQM"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_DENSITY_INTAKE_CLEARANCE: {
    variables: [
      "OPTION_WIDTH_INTAKE_CLEARANCE",
      "STORE_SQM"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_FRESHNESS_INTAKE_TOTAL: {
    variables: [
      "OPTION_WIDTH_INTAKE_TOTAL",
      "OPTION_WIDTH"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_FRESHNESS_INTAKE_MAIN: {
    variables: [
      "OPTION_WIDTH_INTAKE_MAIN",
      "OPTION_WIDTH"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_FRESHNESS_INTAKE_REPLENISHMENT: {
    variables: [
      "OPTION_WIDTH_INTAKE_REPLENISHMENT",
      "OPTION_WIDTH"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_FRESHNESS_INTAKE_INSEASON: {
    variables: [
      "OPTION_WIDTH_INTAKE_INSEASON",
      "OPTION_WIDTH"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_FRESHNESS_INTAKE_CLEARANCE: {
    variables: [
      "OPTION_WIDTH_INTAKE_CLEARANCE",
      "OPTION_WIDTH"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_WIDTH_ACTIVE_RATIO: {
    variables: [
      "OPTION_WIDTH_ACTIVE",
      "OPTION_WIDTH"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_DENSITY_ACTIVE: {
    variables: [
      "OPTION_WIDTH_ACTIVE",
      "STORE_SQM"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPTION_AVG_GIS1: {
    variables: [
      "GIS1",
      "OPTION_WIDTH_ACTIVE"
    ],
    weeklyOnly: true,
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  BEGINNING_OF_PERIOD_AT_COST_PER_SQM: {
    variables: [
      "BEGINNING_OF_PERIOD_AT_COST",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  BEGINNING_OF_PERIOD_UNITS_PER_SQM: {
    variables: [
      "BEGINNING_OF_PERIOD_UNITS",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  COGR_PER_SQM: {
    variables: [
      "COGR",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  END_OF_PERIOD_AT_COST_PER_SQM: {
    variables: [
      "END_OF_PERIOD_AT_COST",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  END_OF_PERIOD_UNITS_PER_SQM: {
    variables: [
      "END_OF_PERIOD_UNITS",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  GIR_PER_SQM: {
    variables: [
      "GIR",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  GIS1_PER_SQM: {
    variables: [
      "GIS1",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  GR2_PER_SQM: {
    variables: [
      "GR2",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  GS1_PER_SQM: {
    variables: [
      "GS1",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  GS1_COGS_PER_SQM: {
    variables: [
      "GS1_COGS",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  GS2_PER_SQM: {
    variables: [
      "GS2",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  GS2_COGS_PER_SQM: {
    variables: [
      "GS2_COGS",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  GS2_MARGIN_VALUE_PER_SQM: {
    variables: [
      "GS2_MARGIN_VALUE",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  GS_DISCOUNT_COST_PER_SQM: {
    variables: [
      "GS_DISCOUNT_COST",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  INTAKE_UNITS_CLEARANCE_PER_SQM: {
    variables: [
      "INTAKE_UNITS_CLEARANCE",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  INTAKE_UNITS_INSEASON_PER_SQM: {
    variables: [
      "INTAKE_UNITS_INSEASON",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  INTAKE_UNITS_MAIN_PER_SQM: {
    variables: [
      "INTAKE_UNITS_MAIN",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  INTAKE_UNITS_REPLENISHMENT_PER_SQM: {
    variables: [
      "INTAKE_UNITS_REPLENISHMENT",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  INTAKE_UNITS_TOTAL_PER_SQM: {
    variables: [
      "INTAKE_UNITS_TOTAL",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  INTAKE_AT_COST_CLEARANCE_PER_SQM: {
    variables: [
      "INTAKE_AT_COST_CLEARANCE",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  INTAKE_AT_COST_INSEASON_PER_SQM: {
    variables: [
      "INTAKE_AT_COST_INSEASON",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  INTAKE_AT_COST_MAIN_PER_SQM: {
    variables: [
      "INTAKE_AT_COST_MAIN",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  INTAKE_AT_COST_REPLENISHMENT_PER_SQM: {
    variables: [
      "INTAKE_AT_COST_REPLENISHMENT",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  INTAKE_AT_COST_TOTAL_PER_SQM: {
    variables: [
      "INTAKE_AT_COST_TOTAL",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  NIS3_PER_SQM: {
    variables: [
      "NIS3",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  NS3_PER_SQM: {
    variables: [
      "NS3",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  NS3_COGS_PER_SQM: {
    variables: [
      "NS3_COGS",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  NS3_MARGIN_VALUE_PER_SQM: {
    variables: [
      "NS3_MARGIN_VALUE",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPEN_TO_BUY_AT_COST_PER_SQM: {
    variables: [
      "OPEN_TO_BUY_AT_COST",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  OPEN_TO_BUY_UNITS_PER_SQM: {
    variables: [
      "OPEN_TO_BUY_UNITS",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  PLANNED_OPEN_TO_BUY_AT_COST_PER_SQM: {
    variables: [
      "PLANNED_OPEN_TO_BUY_AT_COST",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  PLANNED_OPEN_TO_BUY_UNITS_PER_SQM: {
    variables: [
      "PLANNED_OPEN_TO_BUY_UNITS",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  TARGET_STOCK_AT_COST_PER_SQM: {
    variables: [
      "TARGET_STOCK_AT_COST",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  },
  TARGET_STOCK_UNITS_TOTAL_PER_SQM: {
    variables: [
      "TARGET_STOCK_UNITS_TOTAL",
      "STORE_SQM"
    ],
    evaluate: ([a0, a1]) => div(a0, a1)
  }
};
